import React, { useState, useEffect, useRef } from 'react';
import { makeRequest2 } from '../../../utils/makeRequest';
import { toastify } from '../../../utils/toast';
import { 
    getBillerAddressesURL, 
    addBillerAddressURL, 
    updateBillerAddressURL, 
    deleteBillerAddressURL 
} from '../../../utils/urls';
import { Button } from 'primereact/button';

const BillerAddressTab = ({ facilityId }) => {
    const [billerAddress, setBillerAddress] = useState({
        name: '',
        companyName: '',
        website: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        country: 'Kenya',
        postalCode: '',
        logo: null,
        isDefault: false
    });
    
    const [billerAddressList, setBillerAddressList] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [logoPreview, setLogoPreview] = useState(null);
    const [websiteError, setWebsiteError] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (facilityId) {
            console.log('Component mounted with facilityId:', facilityId);
            fetchBillerAddresses();
        } else {
            console.log('No facilityId provided');
        }
    }, [facilityId]);

    const fetchBillerAddresses = async () => {
        try {
            console.log('Fetching biller addresses from:', `${getBillerAddressesURL}/${facilityId}`);
            const response = await makeRequest2(`${getBillerAddressesURL}/${facilityId}`, 'GET');
            
            console.log('Raw response:', response);
            
            if (response && response.success && response.data) {
                console.log('Biller addresses data:', response.data);
                console.log('Data type:', typeof response.data);
                console.log('Is array:', Array.isArray(response.data));
                
                // Handle both array and object responses
                let addresses = [];
                if (Array.isArray(response.data)) {
                    addresses = response.data;
                } else if (typeof response.data === 'object' && response.data !== null) {
                    // If it's an object, try to extract array from common properties
                    if (response.data.data && Array.isArray(response.data.data)) {
                        addresses = response.data.data;
                    } else if (response.data.addresses && Array.isArray(response.data.addresses)) {
                        addresses = response.data.addresses;
                    } else {
                        // If it's a single object, wrap it in an array
                        addresses = [response.data];
                    }
                }
                
                console.log('Final addresses array:', addresses);
                setBillerAddressList(addresses);
            } else if (response && response.data) {
                // Fallback: if response.data exists but success is not explicitly true
                console.log('Using fallback data structure:', response.data);
                console.log('Fallback data type:', typeof response.data);
                
                let addresses = [];
                if (Array.isArray(response.data)) {
                    addresses = response.data;
                } else if (typeof response.data === 'object' && response.data !== null) {
                    addresses = [response.data];
                }
                
                setBillerAddressList(addresses);
            } else {
                console.log('No valid response data structure found:', response);
                setBillerAddressList([]);
            }
        } catch (error) {
            console.error('Error fetching biller addresses:', error);
            console.error('Error response:', error.response?.data);
            toastify('Failed to fetch biller addresses', 'error');
            setBillerAddressList([]);
        }
    };

    // Website validation helper function
    const validateWebsiteUrl = (url) => {
        if (!url || url.trim() === '') return { isValid: true, message: '' }; // Empty is valid since it's optional
        
        const websiteRegex = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;

        if (websiteRegex.test(url.trim())) {
            return { isValid: true, message: '' };
        } else {
            return { 
                isValid: false, 
                message: 'Please enter a valid website URL (e.g., https://example.com, www.example.com, or example.com)' 
            };
        }
    };

    const handleWebsiteChange = (e) => {
        const value = e.target.value;
        setBillerAddress({ ...billerAddress, website: value });
        
        // Validate website URL and show error if invalid
        const validation = validateWebsiteUrl(value);
        setWebsiteError(validation.isValid ? '' : validation.message);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
            if (!allowedTypes.includes(file.type)) {
                toastify('Please select a valid image file (JPEG, PNG, GIF)', 'error');
                return;
            }

            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                toastify('File size must be less than 5MB', 'error');
                return;
            }

            setBillerAddress({ ...billerAddress, logo: file });
            
            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setLogoPreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate website before submitting
        const websiteValidation = validateWebsiteUrl(billerAddress.website);
        if (!websiteValidation.isValid) {
            toastify(websiteValidation.message, 'error');
            return;
        }
        
        setLoading(true);

        try {
            const formData = new FormData();
            
            // Append all form fields
            Object.keys(billerAddress).forEach(key => {
                if (key === 'logo' && billerAddress[key] instanceof File) {
                    formData.append('logo', billerAddress[key]);
                } else if (key !== 'logo') {
                    formData.append(key, billerAddress[key]);
                }
            });

            const url = isEditing
                ? `${updateBillerAddressURL}/${facilityId}/${billerAddress._id}`
                : `${addBillerAddressURL}/${facilityId}`;

            const method = isEditing ? 'PUT' : 'POST';

            console.log('Submitting biller address to:', url, 'Method:', method);
            console.log('Facility ID:', facilityId);

            const response = await makeRequest2(url, method, formData, {
                'Content-Type': 'multipart/form-data'
            });

            console.log('Response:', response);
            console.log('Response type:', typeof response);
            console.log('Response success:', response?.success);
            console.log('Response message:', response?.message);
            console.log('Response error:', response?.error);

            if (response && (response.success || response.message)) {
                toastify(`Biller address ${isEditing ? 'updated' : 'added'} successfully`, 'success');
                fetchBillerAddresses();
                resetForm();
            } else {
                const errorMessage = response?.error || response?.message || 'Operation failed';
                console.error('API Error:', response);
                toastify(errorMessage, 'error');
            }
        } catch (error) {
            console.error('Request Error:', error);
            console.error('Error details:', error.response?.data || error.message);
            
            // Check if it's a network error or response error
            if (error.response) {
                // Server responded with error status
                const errorMessage = error.response.data?.error || error.response.data?.message || `Server error: ${error.response.status}`;
                toastify(errorMessage, 'error');
            } else if (error.request) {
                // Request was made but no response received
                toastify('Network error: Unable to reach server', 'error');
            } else {
                // Something else happened
                toastify('Error saving biller address', 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (address) => {
        setBillerAddress({
            ...address,
            logo: null // Reset file input for editing
        });
        setIsEditing(true);
        setLogoPreview(address.logo ? `/uploads/logos/${address.logo}` : null);
        setWebsiteError(''); // Clear any existing website error
    };

    const handleDelete = async (addressId) => {
        if (window.confirm('Are you sure you want to delete this biller address?')) {
            try {
                console.log('Deleting biller address:', `${deleteBillerAddressURL}/${facilityId}/${addressId}`);
                const response = await makeRequest2(`${deleteBillerAddressURL}/${facilityId}/${addressId}`, 'DELETE');
                
                if (response && (response.success || response.message)) {
                    toastify('Biller address deleted successfully', 'success');
                    fetchBillerAddresses();
                } else {
                    toastify(response?.message || 'Delete failed', 'error');
                }
            } catch (error) {
                console.error('Error deleting biller address:', error);
                toastify('Error deleting biller address', 'error');
            }
        }
    };

    const resetForm = () => {
        setBillerAddress({
            name: '',
            companyName: '',
            website: '',
            email: '',
            phone: '',
            address: '',
            city: '',
            state: '',
            country: 'Kenya',
            postalCode: '',
            logo: null,
            isDefault: false
        });
        setIsEditing(false);
        setLogoPreview(null);
        setWebsiteError('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className="row p-3">
            <div className="col-lg-6">
                <div className="card">
                    <div className="card-body">
                        <h5>{isEditing ? 'Update' : 'Add'} Biller Address</h5>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group mt-3">
                                <label>Address Name*</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={billerAddress.name}
                                    onChange={(e) => setBillerAddress({ ...billerAddress, name: e.target.value })}
                                    placeholder="e.g., Head Office, Branch Office"
                                    required
                                />
                            </div>

                            <div className="form-group mt-3">
                                <label>Company Name*</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={billerAddress.companyName}
                                    onChange={(e) => setBillerAddress({ ...billerAddress, companyName: e.target.value })}
                                    placeholder="Swan Facilities Ltd"
                                    required
                                />
                            </div>

                            <div className="form-group mt-3">
                                <label>Website</label>
                                <input
                                    type="text"
                                    className={`form-control ${websiteError ? 'is-invalid' : ''}`}
                                    value={billerAddress.website}
                                    onChange={handleWebsiteChange}
                                    placeholder="https://www.swanfacilities.com or www.swanfacilities.com"
                                />
                                <small className="text-muted">
                                    Optional. Enter full URL (e.g., https://www.example.com, www.example.com, or example.com)
                                </small>
                                {websiteError && (
                                    <div className="invalid-feedback d-block">
                                        {websiteError}
                                    </div>
                                )}
                            </div>

                            <div className="form-group mt-3">
                                <label>Email*</label>
                                <input
                                    type="email"
                                    className="form-control"
                                    value={billerAddress.email}
                                    onChange={(e) => setBillerAddress({ ...billerAddress, email: e.target.value })}
                                    placeholder="info@swanfacilities.com"
                                    required
                                />
                            </div>

                            <div className="form-group mt-3">
                                <label>Phone</label>
                                <input
                                    type="tel"
                                    className="form-control"
                                    value={billerAddress.phone}
                                    onChange={(e) => setBillerAddress({ ...billerAddress, phone: e.target.value })}
                                    placeholder="+254 XXX XXX XXX"
                                />
                            </div>

                            <div className="form-group mt-3">
                                <label>Address*</label>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    value={billerAddress.address}
                                    onChange={(e) => setBillerAddress({ ...billerAddress, address: e.target.value })}
                                    placeholder="Street address, building, floor, etc."
                                    required
                                ></textarea>
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <div className="form-group mt-3">
                                        <label>City*</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={billerAddress.city}
                                            onChange={(e) => setBillerAddress({ ...billerAddress, city: e.target.value })}
                                            placeholder="Nairobi"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group mt-3">
                                        <label>State/Province</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={billerAddress.state}
                                            onChange={(e) => setBillerAddress({ ...billerAddress, state: e.target.value })}
                                            placeholder="Nairobi County"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <div className="form-group mt-3">
                                        <label>Country*</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={billerAddress.country}
                                            onChange={(e) => setBillerAddress({ ...billerAddress, country: e.target.value })}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group mt-3">
                                        <label>Postal Code</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={billerAddress.postalCode}
                                            onChange={(e) => setBillerAddress({ ...billerAddress, postalCode: e.target.value })}
                                            placeholder="00100"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-group mt-3">
                                <label>Company Logo</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    accept="image/jpeg,image/jpg,image/png,image/gif"
                                />
                                <small className="text-muted">
                                    Supported formats: JPEG, PNG, GIF. Max size: 5MB
                                </small>
                                
                                {logoPreview && (
                                    <div className="mt-2">
                                        <img
                                            src={logoPreview}
                                            alt="Logo preview"
                                            style={{ maxWidth: '150px', maxHeight: '100px', objectFit: 'contain' }}
                                            className="border rounded"
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="form-check mt-3">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={billerAddress.isDefault}
                                    onChange={(e) => setBillerAddress({ ...billerAddress, isDefault: e.target.checked })}
                                    id="defaultAddress"
                                />
                                <label className="form-check-label" htmlFor="defaultAddress">
                                    Set as Default Biller Address
                                </label>
                            </div>

                            <div className="mt-4 text-end">
                                {isEditing && (
                                    <button
                                        type="button"
                                        className="btn btn-secondary me-2"
                                        onClick={resetForm}
                                        disabled={loading}
                                    >
                                        Cancel
                                    </button>
                                )}
                                <button 
                                    type="submit" 
                                    className="btn btn-primary"
                                    disabled={loading || websiteError}
                                >
                                    {loading ? 'Saving...' : (isEditing ? 'Update' : 'Add')} Biller Address
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <div className="col-lg-6">
                <div className="card">
                    <div className="card-body">
                        <h5>Biller Addresses</h5>
                        <div className="table-responsive">
                            <table className="table align-middle">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Company</th>
                                        <th>City</th>
                                        <th>Default</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {billerAddressList.length > 0 ? (
                                        billerAddressList.map((address) => (
                                            <tr key={address._id}>
                                                {/* <td>
                                                    <div className="d-flex align-items-center">
                                                        {address.logo && (
                                                            <img
                                                                src={`/uploads/logos/${address.logo}`}
                                                                alt="Logo"
                                                                style={{ width: '30px', height: '30px', objectFit: 'contain' }}
                                                                className="rounded me-2"
                                                            />
                                                        )}
                                                        {address.name}
                                                    </div>
                                                </td> */}
                                                <td>{address.companyName}</td>
                                                <td>{address.city}</td>
                                                <td>
                                                    {address.isDefault ? (
                                                        <span className="badge bg-success">Yes</span>
                                                    ) : (
                                                        <span className="badge bg-secondary">No</span>
                                                    )}
                                                </td>
                                                <td className="text-end">
                                                    <Button
                                                        icon="ti ti-pencil"
                                                        className="p-button-rounded p-button-warning me-2"
                                                        size="small"
                                                        onClick={() => handleEdit(address)}
                                                        tooltip="Edit"
                                                    />
                                                    <Button
                                                        icon="ti ti-trash"
                                                        className="p-button-rounded p-button-danger"
                                                        size="small"
                                                        onClick={() => handleDelete(address._id)}
                                                        tooltip="Delete"
                                                    />
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center py-4">
                                                <div className="text-muted">
                                                    <i className="ti ti-inbox mb-2" style={{ fontSize: "24px" }}></i>
                                                    <p className="mb-0">No biller addresses available</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BillerAddressTab;